import { useEffect, useState } from 'react'

import {
  Benchmark,
  InvestmentTemplate,
  PortfolioFormChangeValues,
  PortfolioFormPayload,
  PortfolioFormState,
} from 'types'
import { BuyListName } from 'types/buyListTypes'

import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSCard from 'designSystem/components/PDSCard/PDSCard'
import PDSCardHeader from 'designSystem/components/PDSCardHeader/PDSCardHeader'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSHeader from 'designSystem/components/PDSHeader/PDSHeader'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'
import { getPortfolioFormChanges } from 'utils/portfolioUtil'

import InvestmentPreferenceChangeItem from './InvestmentPreferenceChangeItem'
import ConfirmUndoChangesModal from './Modals/ConfirmUndoChangesModal'

type Props = {
  submitEditPortfolioForm: (
    portfolioPayload: PortfolioFormPayload
  ) => Promise<void>
  initialPortfolioFormData: PortfolioFormState
  formState: PortfolioFormState
  benchmarks: Benchmark[]
  buyLists: BuyListName[]
  templates: InvestmentTemplate[]
  undoAllChanges: () => void
}

export default function InvestmentPreferenceChangesSection({
  submitEditPortfolioForm,
  initialPortfolioFormData,
  formState,
  benchmarks,
  buyLists,
  templates,
  undoAllChanges,
}: Props) {
  const [changes, setChanges] = useState<
    Map<string, PortfolioFormChangeValues>
  >(new Map())
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    const newChanges = getPortfolioFormChanges(
      initialPortfolioFormData,
      formState,
      benchmarks,
      buyLists,
      templates
    )
    setChanges(newChanges)
  }, [formState, initialPortfolioFormData, benchmarks, buyLists, templates])

  const confirmUndoAllChanges = () => {
    undoAllChanges()
    setShowModal(false)
  }

  let changesContent
  const hasChanges = changes.size > 0
  if (hasChanges) {
    changesContent = (
      <>
        <PDSText variant="p" fontSize={FONT_SIZE.xs}>
          The following settings have changed:
        </PDSText>
        {Array.from(changes.entries()).map(([label, change], i) => {
          return (
            <InvestmentPreferenceChangeItem
              key={`change-item-${i}`}
              label={label}
              change={change}
            />
          )
        })}
      </>
    )
  } else {
    changesContent = (
      <PDSText variant="p" fontSize={FONT_SIZE.xs}>
        Changes made to investment preferences will appear below.
      </PDSText>
    )
  }

  return (
    <>
      <PDSFlexBox flexDirection="column" gap={SIZE.s12} width="26.625rem">
        <PDSCard>
          <PDSCardHeader>
            <PDSFlexBox flexDirection="column" justifyContent="space-between">
              <PDSHeader variant="h4">Preferences Settings</PDSHeader>
            </PDSFlexBox>
          </PDSCardHeader>
          <PDSFlexBox gap={SIZE.s16} flexDirection="column">
            {changesContent}
            <PDSButton
              disabled={!hasChanges}
              size="md-f"
              variant="tertiary"
              text="Undo All Changes"
              onClick={() => setShowModal(true)}
            />
          </PDSFlexBox>
        </PDSCard>
        <PDSButton
          disabled={!hasChanges}
          form="portfolio-form"
          onClick={() => submitEditPortfolioForm}
          size="md"
          text="Save Investment Preferences"
          variant="primary"
        />
        {hasChanges && (
          <PDSText variant="p" fontSize={FONT_SIZE.xxs} textAlign="center">
            Clicking <b>“Save Investment Preferences”</b> will update your
            investment preferences with the changes shown above.
          </PDSText>
        )}
      </PDSFlexBox>
      <ConfirmUndoChangesModal
        onClose={() => setShowModal(false)}
        onConfirm={confirmUndoAllChanges}
        showModal={showModal}
      />
    </>
  )
}
